// frontend/src/pages/LinkFaculty.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CssBaseline,
  Button,
  Typography,
  Box,
  Divider,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import { setScholarURL, makeUserFacultyLink } from '../services/api';

// Full-page container
const SignInContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  width: '100%',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
  overflow: 'hidden',
  '&::before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    zIndex: -1,
    inset: 0,
    backgroundImage:
      'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
    backgroundRepeat: 'no-repeat',
  },
}));

// Main outer card with offsets from the page edges
const MainCard = styled(Box)(({ theme }) => ({
  width: '95%',
  maxWidth: '1200px',
  margin: '40px auto',
  borderRadius: theme.spacing(2),
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  backgroundColor: '#fff',
  padding: theme.spacing(4),
  position: 'relative',
}));

// Container for candidate cards (displayed in a row)
const CardRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

// Individual candidate card
const CandidateCard = styled(Box)(({ theme, selected }) => ({
  border: selected ? `2px solid ${theme.palette.primary.main}` : '1px solid #ccc',
  borderRadius: theme.spacing(1),
  padding: theme.spacing(2),
  width: '350px',
  cursor: 'pointer',
  transition: 'border-color 0.3s',
  wordWrap: 'break-word',
  overflowWrap: 'anywhere',
  whiteSpace: 'pre-wrap',
  '&:hover': {
    borderColor: theme.palette.primary.main,
  },
}));

// Styled link for Google Scholar URL with proper wrapping
const ScholarLink = styled('a')(({ theme }) => ({
  color: theme.palette.primary.main,
  textDecoration: 'none',
  wordBreak: 'break-word',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

function getFullScholarUrl(link) {
  if (!link) return '';
  if (link.startsWith('http')) {
    return link;
  }
  return `https://scholar.google.com${link}`;
}

export default function FacultyUserLink({ onLinkSuccess }) {
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState([]);
  const [isCandidatesLoading, setIsCandidatesLoading] = useState(true);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch candidate matches on mount.
  useEffect(() => {
    async function fetchCandidates() {
      try {
        const response = await setScholarURL();
        if (response.success && response.candidate_matches) {
          setCandidates(response.candidate_matches);
        }
      } catch (error) {
        console.error('Failed to fetch candidate matches:', error);
      } finally {
        setIsCandidatesLoading(false);
      }
    }
    fetchCandidates();
  }, []);

  const refreshUserData = async () => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/users/me/`,
        { withCredentials: true }
      );
      onLinkSuccess(response.data);
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const payload = { candidate_id: selectedCandidate };
      const linkResponse = await makeUserFacultyLink(payload);
      if (linkResponse.success) {
        await refreshUserData();
        navigate('/home', { replace: true });
      } else {
        console.error('Faculty linking failed:', linkResponse);
        setLoading(false);
      }
    } catch (error) {
      console.error('API request failed during faculty linking:', error);
      setLoading(false);
    }
  };

  const handleNone = async () => {
    setLoading(true);
    try {
      const linkResponse = await makeUserFacultyLink({ candidate_id: null });
      if (linkResponse.success) {
        await refreshUserData();
        navigate('/home', { replace: true });
      } else {
        console.error('Faculty linking (none) failed:', linkResponse);
        setLoading(false);
      }
    } catch (error) {
      console.error('API request failed during faculty linking (none):', error);
      setLoading(false);
    }
  };

  // If loading is true (after confirm/none is clicked), show a full-page loading screen.
  if (loading) {
    return (
      <>
        <CssBaseline />
        <SignInContainer>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '100vh',
            }}
          >
            <CircularProgress />
          </Box>
        </SignInContainer>
      </>
    );
  }

  return (
    <>
      <CssBaseline />
      <SignInContainer>
        <MainCard>
          <Typography variant="h5" gutterBottom align="center">
            Please select your academic profile
          </Typography>
          {isCandidatesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <CircularProgress size={40} />
            </Box>
          ) : candidates.length > 0 ? (
            <>
              <Typography variant="body1" align="center">
                Choose one of the options below.
              </Typography>
              <CardRow>
                {candidates.map((candidate) => {
                  const publications = candidate.abstracts || [];
                  const fullScholarUrl = getFullScholarUrl(candidate.scholar_link);
                  return (
                    <CandidateCard
                      key={candidate.id}
                      selected={selectedCandidate === candidate.id}
                      onClick={() => setSelectedCandidate(candidate.id)}
                    >
                      <Typography variant="subtitle1" gutterBottom>
                        {candidate.name}
                      </Typography>
                      <ScholarLink
                        href={fullScholarUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {fullScholarUrl}
                      </ScholarLink>
                      <Divider sx={{ my: 1 }} />
                      {publications.length > 0 ? (
                        <Box component="ul" sx={{ pl: 2, m: 0 }}>
                          {publications.slice(0, 3).map((title, idx) => (
                            <Box component="li" key={idx} sx={{ mb: 0.5 }}>
                              <Typography variant="body2">{title}</Typography>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          No publications available.
                        </Typography>
                      )}
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="caption" color="textSecondary">
                        *Publications may not be perfect
                      </Typography>
                    </CandidateCard>
                  );
                })}
              </CardRow>
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleConfirm}
                  sx={{ mr: 2 }}
                  disabled={!selectedCandidate || loading}
                >
                  Confirm Selection
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleNone}
                  disabled={loading}
                >
                  None of these are me
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Typography variant="body1" align="center" sx={{ mb: 2 }}>
                We couldn't find a matching profile.
              </Typography>
              <Box sx={{ textAlign: 'center' }}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleNone}
                  disabled={loading}
                >
                  Continue with Blank Profile
                </Button>
              </Box>
            </>
          )}
        </MainCard>
      </SignInContainer>
    </>
  );
}
